import { ref, readonly, nextTick } from 'vue';
import { useChartStore } from '@/stores/chartStore';
import { useLayoutStore } from '@/stores/layoutStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useActionResultMessages } from '@/composables/useActionResultMessages';
import { useAutoRefresh } from '@/composables/useAutoRefresh';
import { useInspector } from '@/composables/useInspector';
import { useModals } from '@/composables/useModals';
import { useSymbolInformation } from '@/composables/useSymbolInformation';
import { useLoadChartsAndChartStyles } from '@/composables/useLoadChartsAndChartStyles';
import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
import { getChart } from '@/composables/getChart';
import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';
import { loadExternalCodeFile } from '@/utils/loadExternalCodeFile';
import { waitForChartImageToRender } from '@/utils/waitForChartImageToRender';
import { buildUrlString } from '@/utils/buildUrlString';
import { CHART_IMAGE_ID } from '@/enums/html-ids';
import { CHART_TYPE_OPTION_PERF_CHART } from '@/enums/chart-type-options';

window.chartNotes = null;

const isActivatingAnnotationMode = ref(false);
const isAnnotating = ref(false);
const isDeactivatingAnnotationMode = ref(false);
const isChartNotesV2Initialized = ref(false);
let hasInitializedChartNotesV2 = false;
let shouldTurnInspectorOnWhenChartNotesClosed = false;

function unInitializeChartNotesV2() {
  isChartNotesV2Initialized.value = false;
}

async function loadChartNotesV2DependencyScripts() {
  const jqueryUrl = buildUrlString('/vendor/jquery/3.6.0/jquery.min.js', { domain: 'd.stockcharts.com' });
  const jqueryUICssUrl1 = buildUrlString('/vendor/jquery-ui/1.13.1/themes/base/jquery-ui.css', { domain: 'd.stockcharts.com' });
  const jqueryUICssUrl2 = buildUrlString('/vendor/jquery-ui/1.13.0/jquery-ui.min.css', { domain: 'd.stockcharts.com' });
  const spectrumCssUrl = buildUrlString('/vendor/spectrum/1.8.0/spectrum.min.css', { domain: 'd.stockcharts.com' });
  const jqueryUIUrl = buildUrlString('/vendor/jquery-ui/1.13.0/jquery-ui.min.js', { domain: 'd.stockcharts.com' });
  const spectrumScriptUrl = buildUrlString('/chartnotes/js/spectrum/spectrum.js', { domain: 'd.stockcharts.com' });
  const commonsScriptUrl = buildUrlString('/chartnotes/js/commons.js', { domain: 'd.stockcharts.com' });
  const chartnotesScriptUrl = buildUrlString('/chartnotes/js/chartnotes.js', { domain: 'd.stockcharts.com' });
  const stylepickerScriptUrl = buildUrlString('/chartnotes/js/stylepicker.js', { domain: 'd.stockcharts.com' });
  const uiScriptUrl = buildUrlString('/chartnotes/js/ui.js', { domain: 'd.stockcharts.com' });
  const geometryScriptUrl = buildUrlString('/chartnotes/js/geometry.js', { domain: 'd.stockcharts.com' });

  await loadExternalCodeFile(jqueryUrl, 3, 'js');
  await loadExternalCodeFile(jqueryUICssUrl1, 3, 'css');
  await loadExternalCodeFile(jqueryUICssUrl2, 3, 'css');
  await loadExternalCodeFile(spectrumCssUrl, 3, 'css');
  await loadExternalCodeFile(jqueryUIUrl, 3, 'js');
  await loadExternalCodeFile(spectrumScriptUrl, 3, 'js');
  await loadExternalCodeFile(commonsScriptUrl, 3, 'js');
  await loadExternalCodeFile(chartnotesScriptUrl, 3, 'js');
  await loadExternalCodeFile(stylepickerScriptUrl, 3, 'js');
  await loadExternalCodeFile(uiScriptUrl, 3, 'js');
  await loadExternalCodeFile(geometryScriptUrl, 3, 'js');
}

async function createChartNotesV2() {
  const chartStore = useChartStore();
  const { isLoggedIn, chartCode } = useAuthentication();
  const { chartBeingViewed, isChartBeingViewedReadonly } = useWorkbenchSelections();
  const chartImageElement = document.getElementById(CHART_IMAGE_ID);

  if (!chartImageElement) {
    throw new Error(`Failed to create ChartNotes object - unable to obtain a reference to the html chart image element with id: ${CHART_IMAGE_ID}.`);
  }

  await Promise.all([
    getChart(chartStore.recidOfChartBeingViewed, true),
    waitForChartImageToRender(chartImageElement)
  ]);

  const chartNotesV2OptionsObject = {
    boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.0)',
    colorBackground: 'rgb(255, 255, 255)',
    border: '1px solid rgb(174, 185, 197)',
    is10pp: false,
    chartCode: '',
    onSaveNewChart: handleChartNotesV2Closed
  };

  if (isLoggedIn.value) {
    chartNotesV2OptionsObject.chartCode = chartCode.value;

    if (chartBeingViewed.value) {
      chartNotesV2OptionsObject.recid = chartBeingViewed.value.recid;
      chartNotesV2OptionsObject.isReadOnlyChart = isChartBeingViewedReadonly.value;
      chartNotesV2OptionsObject.selectedChartName = chartBeingViewed.value.name;
    }
  }

  window.chartNotes = new ChartNotes(chartImageElement, chartNotesV2OptionsObject);
}

async function handleChartNotesV2Closed(chartRecid, wasChartNotesClosedWithoutSaving) {
  const { isLoggedIn } = useAuthentication();
  const { loadChart } = useLoadChartsAndChartStyles();

  delete window.chartNotes.settings.onSaveNewChart;

  const chart = await getChart(chartRecid, true);

  if (
    !chart ||
    wasChartNotesClosedWithoutSaving ||
    !isLoggedIn.value
  ) {
    return;
  }

  isDeactivatingAnnotationMode.value = true;

  await loadChart(chart.recid);
}

async function toggleAnnotate() {
  const chartStore = useChartStore();
  const { refreshChart } = useAutoRefresh();
  const { setIsInspecting } = useInspector();

  isDeactivatingAnnotationMode.value = true;

  $('#ChartNotesMenu').fadeOut('slow');

  if (window.chartNotes) {
    window.chartNotes.startAnnotate();
  }

  $('.popbox').remove();

  if (
    window.chartNotes &&
    typeof window.chartNotes.settings.onSaveNewChart === 'function'
  ) {
    await window.chartNotes.settings.onSaveNewChart(chartStore.recidOfChartBeingViewed, true);
  }

  window.chartNotes = null;
  isAnnotating.value = false;
  isDeactivatingAnnotationMode.value = false;

  setIsInspecting(shouldTurnInspectorOnWhenChartNotesClosed);
  refreshChart();
}

async function initializeChartNotesV2() {
  const { addRedActionResultMessage } = useActionResultMessages();

  if (hasInitializedChartNotesV2) {
    return;
  }

  try {
    await loadChartNotesV2DependencyScripts();

    window.toggleAnnotate = toggleAnnotate;

    hasInitializedChartNotesV2 = true;

    return;
  } catch (e) {
    window.sc3WorkbenchLogger.error('Failed to initialize ChartNotes v2 - error occurred while loading ChartNotes v2 dependency scripts.', e);
  }

  addRedActionResultMessage('Failed to load ChartNotes.');
}

async function activateAnnotationMode() {
  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const { addRedActionResultMessage } = useActionResultMessages();
  const { refreshChart } = useAutoRefresh();
  const { setIsInspecting } = useInspector();
  const { closeModal } = useModals();
  const { isSymbolBeingViewedNotFound, isSymbolBeingViewedAnEODSymbolOnAnIntradayChart } = useSymbolInformation();

  if (
    isAnnotating.value ||
    isActivatingAnnotationMode.value ||
    isDeactivatingAnnotationMode.value ||
    !layoutStore.isChartComponentMounted ||
    isSymbolBeingViewedNotFound.value ||
    isSymbolBeingViewedAnEODSymbolOnAnIntradayChart.value ||
    chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value
  ) {
    return;
  }

  closeModal();

  shouldTurnInspectorOnWhenChartNotesClosed = layoutStore.isInspecting;

  setIsInspecting(false);

  isActivatingAnnotationMode.value = true;

  await initializeChartNotesV2();

  if (!hasInitializedChartNotesV2) {
    isActivatingAnnotationMode.value = false;

    setIsInspecting(shouldTurnInspectorOnWhenChartNotesClosed);
    refreshChart();
    return;
  }

  isAnnotating.value = true;

  await nextTick();
  await waitForDomToRepaint();

  try {
    await createChartNotesV2();
  } catch (e) {
    isActivatingAnnotationMode.value = false;

    window.sc3WorkbenchLogger.error('Failed to initialize ChartNotes v2 - error occurred while creating ChartNotes object.', e);

    addRedActionResultMessage('Failed to load ChartNotes.');

    window.toggleAnnotate();

    return;
  }

  $('#ChartNotesMenu').fadeIn('slow');

  window.chartNotes.startAnnotate();

  await waitForDomToRepaint();

  isActivatingAnnotationMode.value = false;

  if (isAnnotating.value) {
    isChartNotesV2Initialized.value = true;
  }
}

function forceCloseChartNotesV2() {
  if (!window.chartNotes) {
    return;
  }

  delete window.chartNotes.settings.onSaveNewChart;

  window.chartNotes.ui.saveHilited = false;

  window.chartNotes.onCloseRequest();
}

function isChartNotesFullyInitialized() {
  return (
    !isActivatingAnnotationMode.value &&
    isAnnotating.value &&
    !isDeactivatingAnnotationMode.value &&
    isChartNotesV2Initialized.value &&
    !!window.chartNotes &&
    typeof window.chartNotes.onCloseRequest === 'function' &&
    !!window.chartNotes.easel
  );
}

export function useChartNotesV2() {
  return {
    isActivatingAnnotationMode: readonly(isActivatingAnnotationMode),
    isAnnotating: readonly(isAnnotating),
    isDeactivatingAnnotationMode: readonly(isDeactivatingAnnotationMode),
    isChartNotesV2Initialized: readonly(isChartNotesV2Initialized),
    unInitializeChartNotesV2,
    activateAnnotationMode,
    forceCloseChartNotesV2,
    isChartNotesFullyInitialized
  };
}
